import * as React from 'react';
import {ChevronDown} from 'lucide-react';
import {useTranslation} from 'react-i18next';
import {cn} from '../../lib/cn';
import {toCssSize} from '../../lib/utils';
import {isMobileModalModeAvailable} from '../../lib/runtimeSettings';
import {useIsMobile, useOutsideClick} from '../../hooks';
import {FieldErrorMessage} from '../../field-error-message';
import {Fieldset} from '../_fieldset';
import {AirbnbDatePickerContent} from '../../airbnb-fields/datepicker/DatePickerContent';
import {useDatePickerWheel} from '../../airbnb-fields/datepicker/useDatePickerWheel';
import {normalizeDateValue} from '../../airbnb-fields/datepicker/datePicker.utils';
import {useMonthCombobox} from './useMonthCombobox';

const MONTHS_IN_YEAR = 12;

function getMonthLabels(locale: string) {
  const formatter = new Intl.DateTimeFormat(locale, {month: 'long'});
  return Array.from({length: MONTHS_IN_YEAR}, (_, monthIndex) =>
    formatter.format(new Date(2024, monthIndex, 1)),
  );
}

const DAY_PATTERN = /^(0?[1-9]|[12]\d|3[01])$/;
// Allows empty, a single leading digit, or a complete 01–31 day — rejects out-of-range
// partials like "00", "32" or "44" as they are typed.
const PARTIAL_DAY_PATTERN = /^([0-9]|0[1-9]|[12]\d|3[01])?$/;
const PARTIAL_YEAR_PATTERN = /^[0-9]{0,4}$/;
const COMPLETE_YEAR_PATTERN = /^\d{4}$/;

// Accepted year window, expressed as offsets from the current year so the bounds
// shift over time and never need a manual update (e.g. in 2026 → 1906–2076).
const MIN_YEAR_OFFSET = 120;
const MAX_YEAR_OFFSET = 50;

function getYearBounds() {
  const currentYear = new Date().getFullYear();
  return {
    minYear: currentYear - MIN_YEAR_OFFSET,
    maxYear: currentYear + MAX_YEAR_OFFSET,
  };
}

type DatepickerDate = Date | null;

type DatepickerSubfield = 'day' | 'month' | 'year';

type DatepickerHandle = {
  getDayValue: () => string;
  getYearValue: () => string;
  setDayValue: (value: string) => void;
  setYearValue: (value: string) => void;
  focus: () => void;
};

type DatepickerProps = {
  label?: string;
  value?: DatepickerDate;
  defaultValue?: DatepickerDate;
  onChange?: (value: DatepickerDate, name?: string) => void;
  onFocus?: React.FocusEventHandler<HTMLInputElement | HTMLButtonElement>;
  onFieldFocus?: (subfield: DatepickerSubfield) => void;
  onFieldBlur?: (subfield: DatepickerSubfield) => void;
  name?: string;
  invalid?: boolean;
  error?: string;
  disabled?: boolean;
  readOnly?: boolean;
  loading?: boolean;
  optional?: boolean | string;
  tooltip?: React.ReactNode;
  className?: string;
  monthLabels?: string[];
  dayPlaceholder?: string;
  yearPlaceholder?: string;
  monthPlaceholder?: string;
  mobilePlaceholder?: string;
  mobileTitle?: string;
  doneLabel?: string;
  hideErrorMessage?: boolean;
  helperText?: string;
  width?: number | string;
  locale?: string;
  minDate?: Date;
  maxDate?: Date;
  formatValue?: (date: Date) => string;
};

function isValidCalendarDate(year: number, monthIndex: number, day: number) {
  const date = new Date(year, monthIndex, day);
  return (
    date.getFullYear() === year &&
    date.getMonth() === monthIndex &&
    date.getDate() === day
  );
}

function getDaysInMonth(monthIndex: number, year: string): number {
  const yearNum = COMPLETE_YEAR_PATTERN.test(year) ? parseInt(year, 10) : 2024;
  return new Date(yearNum, monthIndex + 1, 0).getDate();
}

function clampDayToMonth(day: string, monthIndex: number | null, year: string): string {
  if (!day || monthIndex === null) return day;
  const dayNum = parseInt(day, 10);
  if (Number.isNaN(dayNum)) return day;
  const max = getDaysInMonth(monthIndex, year);
  return dayNum > max ? String(max) : day;
}

function partsFromDate(date: DatepickerDate) {
  if (!date) return {day: '', monthIndex: null as number | null, year: ''};
  return {
    day: String(date.getDate()),
    monthIndex: date.getMonth(),
    year: String(date.getFullYear()),
  };
}

function datepickerDatesEqual(a: DatepickerDate, b: DatepickerDate): boolean {
  if (!(a instanceof Date) || !(b instanceof Date)) return a === b;
  return a.getTime() === b.getTime();
}

function isYearInRange(yearNum: number, minYear: number, maxYear: number): boolean {
  return yearNum >= minYear && yearNum <= maxYear;
}

function dateFromParts(
  day: string,
  monthIndex: number | null,
  year: string,
  minYear: number,
  maxYear: number,
): Date | null {
  if (!day || monthIndex === null || !year) return null;
  if (!DAY_PATTERN.test(day) || !COMPLETE_YEAR_PATTERN.test(year)) return null;
  const dayNum = parseInt(day, 10);
  const yearNum = parseInt(year, 10);
  if (!isYearInRange(yearNum, minYear, maxYear)) return null;
  if (!isValidCalendarDate(yearNum, monthIndex, dayNum)) return null;
  return new Date(yearNum, monthIndex, dayNum);
}

const Datepicker = React.forwardRef<DatepickerHandle, DatepickerProps>(
  function Datepicker(
    {
      label,
      value,
      defaultValue,
      onChange,
      onFocus,
      onFieldFocus,
      onFieldBlur,
      name,
      invalid,
      error,
      disabled,
      readOnly,
      loading,
      optional,
      tooltip,
      className,
      monthLabels,
      dayPlaceholder = 'DD',
      yearPlaceholder = 'YYYY',
      monthPlaceholder,
      mobilePlaceholder,
      mobileTitle,
      doneLabel,
      hideErrorMessage,
      helperText,
      width,
      locale,
      minDate,
      maxDate,
      formatValue,
    },
    ref,
  ) {
    const containerRef = React.useRef<HTMLDivElement>(null);
    const dayInputRef = React.useRef<HTMLInputElement>(null);
    const monthInputRef = React.useRef<HTMLInputElement>(null);
    const yearInputRef = React.useRef<HTMLInputElement>(null);
    const mobileTriggerRef = React.useRef<HTMLButtonElement>(null);
    const reactId = React.useId();
    const baseId = name ?? `dash-datepicker-${reactId}`;
    const dayId = `${baseId}-day`;
    const monthId = `${baseId}-month`;
    const yearId = `${baseId}-year`;
    const labelId = `${baseId}-label`;
    const errorId = `${baseId}-error`;
    const wheelBaseId = `${baseId}-wheel`;

    const {t, i18n} = useTranslation();
    const resolvedLocale = locale ?? i18n.resolvedLanguage ?? i18n.language ?? 'en-US';
    const resolvedMonthLabels = React.useMemo(
      () => monthLabels ?? getMonthLabels(resolvedLocale),
      [resolvedLocale, monthLabels],
    );
    const resolvedMonthPlaceholder = monthPlaceholder ?? t('month');
    const resolvedDoneLabel = doneLabel ?? t('done');

    const isControlled = value !== undefined;
    const initialParts = React.useMemo(
      () => partsFromDate(normalizeDateValue(value) ?? normalizeDateValue(defaultValue)),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [],
    );

    const [day, setDay] = React.useState<string>(initialParts.day);
    const [monthIndex, setMonthIndex] = React.useState<number | null>(
      initialParts.monthIndex,
    );
    const [year, setYear] = React.useState<string>(initialParts.year);
    const [isWheelOpen, setIsWheelOpen] = React.useState(false);
    const [focusedField, setFocusedField] = React.useState<
      'day' | 'month' | 'year' | null
    >(null);
    const isMobile = useIsMobile() && isMobileModalModeAvailable();

    const emitChangeRef = React.useRef<
      (nextDay: string, nextMonth: number | null, nextYear: string) => void
    >(() => {});
    // The last date we emitted upstream. Lets the controlled-value sync ignore our
    // own emissions so typing a partial date (e.g. mid-edit year) is not wiped out
    // when the parent reflects the in-progress `null` back as `value`.
    const lastEmittedRef = React.useRef<DatepickerDate>(normalizeDateValue(value));
    const dayStateRef = React.useRef(day);
    const yearStateRef = React.useRef(year);
    const monthIndexRef = React.useRef(monthIndex);
    dayStateRef.current = day;
    yearStateRef.current = year;
    monthIndexRef.current = monthIndex;

    React.useImperativeHandle(
      ref,
      () => ({
        focus: () => {
          const focusable =
            mobileTriggerRef.current ??
            dayInputRef.current ??
            monthInputRef.current ??
            yearInputRef.current;
          if (focusable) {
            focusable.focus();
          } else {
            containerRef.current?.scrollIntoView({block: 'center'});
          }
        },
        getDayValue: () => dayStateRef.current,
        getYearValue: () => yearStateRef.current,
        setDayValue: (next: string) => {
          if (!PARTIAL_DAY_PATTERN.test(next)) return;
          const month = monthIndexRef.current;
          if (
            month !== null &&
            next &&
            parseInt(next, 10) > getDaysInMonth(month, yearStateRef.current)
          ) {
            return;
          }
          setDay(next);
          emitChangeRef.current(next, month, yearStateRef.current);
        },
        setYearValue: (next: string) => {
          if (!PARTIAL_YEAR_PATTERN.test(next)) return;
          const clampedDay = clampDayToMonth(
            dayStateRef.current,
            monthIndexRef.current,
            next,
          );
          if (clampedDay !== dayStateRef.current) setDay(clampedDay);
          setYear(next);
          emitChangeRef.current(clampedDay, monthIndexRef.current, next);
        },
      }),
      [],
    );

    React.useEffect(() => {
      if (!isControlled) return;
      const incoming = normalizeDateValue(value);
      // Skip when the parent is merely echoing back the value we just emitted —
      // otherwise an in-progress (incomplete → null) edit clears the whole field.
      if (datepickerDatesEqual(incoming, lastEmittedRef.current)) return;
      lastEmittedRef.current = incoming;
      const next = partsFromDate(incoming);
      setDay(next.day);
      setMonthIndex(next.monthIndex);
      setYear(next.year);
    }, [isControlled, value]);

    const {minYear, maxYear} = React.useMemo(() => getYearBounds(), []);

    const yearError = React.useMemo(() => {
      if (year.length < 4) return null;
      const yearNum = parseInt(year, 10);
      if (!isYearInRange(yearNum, minYear, maxYear)) {
        return t('year_out_of_range', {min: minYear, max: maxYear});
      }
      return null;
    }, [year, minYear, maxYear, t]);

    const resolvedError = error || yearError || undefined;

    const isEmpty = !day && monthIndex === null && !year;
    const isBlocked = Boolean(disabled) || Boolean(loading);
    const isInvalid = Boolean(invalid || resolvedError);
    const wrapperWidth = toCssSize(width);
    const errorDescribedBy = resolvedError && !hideErrorMessage ? errorId : undefined;

    const currentDate = React.useMemo(
      () => dateFromParts(day, monthIndex, year, minYear, maxYear),
      [day, monthIndex, year, minYear, maxYear],
    );

    const emitChange = React.useCallback(
      (nextDay: string, nextMonth: number | null, nextYear: string) => {
        const date = dateFromParts(nextDay, nextMonth, nextYear, minYear, maxYear);
        lastEmittedRef.current = date;
        onChange?.(date, name);
      },
      [name, onChange, minYear, maxYear],
    );
    emitChangeRef.current = emitChange;

    const commitMonth = React.useCallback(
      (index: number | null) => {
        const clampedDay = clampDayToMonth(day, index, year);
        if (clampedDay !== day) setDay(clampedDay);
        setMonthIndex(index);
        emitChange(clampedDay, index, year);
      },
      [day, emitChange, year],
    );

    const handleMonthSelect = React.useCallback(
      (index: number) => {
        commitMonth(index);
        yearInputRef.current?.focus();
      },
      [commitMonth],
    );

    const handleMonthClear = React.useCallback(() => commitMonth(null), [commitMonth]);

    const monthCombobox = useMonthCombobox({
      monthLabels: resolvedMonthLabels,
      monthIndex,
      isFieldFocused: focusedField === 'month',
      disabled: isBlocked,
      onSelect: handleMonthSelect,
      onCommit: commitMonth,
      onClear: handleMonthClear,
    });

    const isFocused = focusedField !== null || monthCombobox.isOpen || isWheelOpen;

    useOutsideClick({
      elementRef: containerRef,
      onOutsideClick: () => monthCombobox.setIsOpen(false),
      isDisabled: !monthCombobox.isOpen || isMobile,
    });

    const handleDayChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const next = event.target.value;
      if (!PARTIAL_DAY_PATTERN.test(next)) return;
      if (
        monthIndex !== null &&
        next &&
        parseInt(next, 10) > getDaysInMonth(monthIndex, year)
      ) {
        return;
      }
      setDay(next);
      emitChange(next, monthIndex, year);
      if (next.length === 2 && DAY_PATTERN.test(next)) {
        monthInputRef.current?.focus();
      }
    };

    const handleYearChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const next = event.target.value;
      if (!PARTIAL_YEAR_PATTERN.test(next)) return;
      const clampedDay = clampDayToMonth(day, monthIndex, next);
      if (clampedDay !== day) setDay(clampedDay);
      setYear(next);
      emitChange(clampedDay, monthIndex, next);
    };

    const focusDayInput = React.useCallback(() => {
      if (isBlocked || readOnly) return;
      dayInputRef.current?.focus();
    }, [isBlocked, readOnly]);

    const openWheel = React.useCallback(() => {
      if (isBlocked || readOnly) return;
      setIsWheelOpen(true);
    }, [isBlocked, readOnly]);

    const closeWheel = React.useCallback(() => {
      setIsWheelOpen(false);
      mobileTriggerRef.current?.focus();
    }, []);

    const showCoverage = isEmpty && !isFocused && !isMobile;

    const resolvedMinDate = React.useMemo(
      () => normalizeDateValue(minDate) ?? undefined,
      [minDate],
    );
    const resolvedMaxDate = React.useMemo(
      () => normalizeDateValue(maxDate) ?? undefined,
      [maxDate],
    );

    const wheel = useDatePickerWheel({
      isOpen: isWheelOpen,
      value: currentDate,
      defaultValue: null,
      minDate: resolvedMinDate,
      maxDate: resolvedMaxDate,
    });

    const handleWheelDone = React.useCallback(() => {
      const next = wheel.draftDate;
      setDay(String(next.getDate()));
      setMonthIndex(next.getMonth());
      setYear(String(next.getFullYear()));
      lastEmittedRef.current = next;
      onChange?.(next, name);
      setIsWheelOpen(false);
      mobileTriggerRef.current?.focus();
    }, [name, onChange, wheel.draftDate]);

    const defaultFormatValue = React.useCallback(
      (date: Date) =>
        `${date.getDate()} ${resolvedMonthLabels[date.getMonth()]} ${date.getFullYear()}`,
      [resolvedMonthLabels],
    );

    const triggerText = currentDate
      ? (formatValue ?? defaultFormatValue)(currentDate)
      : undefined;

    const monthListboxId = `${monthId}-listbox`;
    const getMonthOptionId = (index: number) => `${monthId}-option-${index}`;

    const monthPanelContent =
      monthCombobox.filteredMonths.length === 0 ? (
        <div className="px-4 py-3 text-left text-[14px] font-medium text-[var(--chekin-color-gray-1)]">
          {t('no_options')}
        </div>
      ) : (
        <ul
          id={monthListboxId}
          role="listbox"
          aria-labelledby={labelId}
          className="m-0 max-h-[260px] list-none overflow-y-auto p-1"
        >
          {monthCombobox.filteredMonths.map((option, position) => {
            const isSelected = option.index === monthIndex;
            const isHighlighted = position === monthCombobox.highlightIndex;
            return (
              <li key={option.index} role="presentation">
                <button
                  id={getMonthOptionId(option.index)}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  tabIndex={-1}
                  onMouseDown={event => event.preventDefault()}
                  onMouseMove={() => monthCombobox.setHighlightIndex(position)}
                  onClick={() => monthCombobox.selectMonth(option.index)}
                  className={cn(
                    'flex w-full items-center justify-start rounded-md border-0 bg-transparent px-3 py-[10px] text-left text-[16px] font-medium text-[var(--chekin-color-brand-navy)] outline-none transition-colors',
                    isHighlighted &&
                      'bg-[var(--chekin-color-surface-pressed)] text-[var(--chekin-color-brand-blue)]',
                    isSelected && 'font-bold',
                  )}
                >
                  {option.label}
                </button>
              </li>
            );
          })}
        </ul>
      );

    const subInputClass = cn(
      'm-0 box-border h-full w-full min-w-0 border-0 bg-transparent text-center text-[16px] font-medium leading-5 text-[var(--chekin-color-brand-navy)] outline-none placeholder:text-[length:var(--field-placeholder-font-size)] placeholder:font-[var(--field-placeholder-font-weight)] placeholder:text-[var(--chekin-color-gray-1)]',
      isBlocked && 'cursor-not-allowed',
      loading && 'cursor-progress',
    );

    return (
      <div
        ref={containerRef}
        className={cn(
          'relative w-[var(--field-width,100%)] max-w-[var(--field-max-width,296px)]',
          disabled && 'cursor-not-allowed opacity-50',
          loading && 'cursor-progress opacity-50',
          className,
        )}
        style={wrapperWidth ? {width: wrapperWidth} : undefined}
      >
        <div className="relative min-h-[var(--field-min-height,48px)] w-full">
          <div className="relative w-full">
            {isMobile ? (
              <button
                ref={mobileTriggerRef}
                id={dayId}
                type="button"
                aria-haspopup="dialog"
                aria-expanded={isWheelOpen}
                aria-labelledby={labelId}
                aria-invalid={isInvalid}
                aria-describedby={errorDescribedBy}
                aria-busy={loading}
                disabled={isBlocked || readOnly}
                onClick={openWheel}
                onFocus={onFocus}
                className={cn(
                  'relative m-0 box-border flex h-12 w-full cursor-pointer items-center justify-between gap-2 rounded-[6px] border-0 px-4 text-left text-[16px] font-medium leading-5 outline-none transition-colors duration-200',
                  triggerText
                    ? 'bg-transparent text-[var(--chekin-color-brand-navy)]'
                    : 'bg-[var(--empty-field-background)] text-[var(--chekin-color-gray-1)]',
                  (isBlocked || readOnly) && 'cursor-not-allowed',
                )}
              >
                <span className="block min-w-0 flex-1 truncate text-left">
                  {triggerText ?? (isWheelOpen ? mobilePlaceholder : null)}
                </span>
                <span className="pointer-events-none flex items-center gap-2 text-[var(--chekin-color-gray-2)]">
                  <ChevronDown
                    size={16}
                    className={cn(
                      'transition-transform duration-200',
                      isWheelOpen && 'rotate-180 text-[var(--chekin-color-brand-blue)]',
                    )}
                  />
                </span>
              </button>
            ) : (
              <div
                className={cn(
                  'relative box-border grid h-12 w-full grid-cols-[minmax(0,1fr)_minmax(96px,140px)_minmax(0,1fr)] items-center rounded-[6px] text-[16px] font-medium text-[var(--chekin-color-brand-navy)]',
                )}
              >
                <div className="flex h-full min-w-0 items-center px-2">
                  <input
                    ref={dayInputRef}
                    id={dayId}
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    name={name ? `${name}-day` : undefined}
                    value={day}
                    placeholder={dayPlaceholder}
                    disabled={isBlocked}
                    readOnly={readOnly}
                    aria-invalid={isInvalid}
                    aria-describedby={errorDescribedBy}
                    aria-labelledby={labelId}
                    onChange={handleDayChange}
                    onFocus={event => {
                      onFocus?.(event);
                      onFieldFocus?.('day');
                      setFocusedField('day');
                    }}
                    onBlur={() => {
                      onFieldBlur?.('day');
                      setFocusedField(null);
                    }}
                    maxLength={2}
                    className={subInputClass}
                  />
                </div>
                <div className="relative flex h-full min-w-0 items-center gap-1 px-2 before:absolute before:inset-y-3 before:left-0 before:w-px before:bg-[var(--chekin-color-gray-3)] before:content-[''] after:absolute after:inset-y-3 after:right-0 after:w-px after:bg-[var(--chekin-color-gray-3)] after:content-[''] sm:px-3">
                  <input
                    ref={monthInputRef}
                    id={monthId}
                    type="text"
                    role="combobox"
                    autoComplete="off"
                    aria-haspopup="listbox"
                    aria-expanded={monthCombobox.isOpen}
                    aria-controls={monthListboxId}
                    aria-autocomplete="list"
                    aria-activedescendant={
                      monthCombobox.highlightIndex >= 0 &&
                      monthCombobox.filteredMonths[monthCombobox.highlightIndex]
                        ? getMonthOptionId(
                            monthCombobox.filteredMonths[monthCombobox.highlightIndex]
                              .index,
                          )
                        : undefined
                    }
                    aria-labelledby={labelId}
                    aria-invalid={isInvalid}
                    aria-describedby={errorDescribedBy}
                    name={name ? `${name}-month` : undefined}
                    value={monthCombobox.inputValue}
                    placeholder={resolvedMonthPlaceholder}
                    disabled={isBlocked}
                    readOnly={readOnly}
                    onChange={monthCombobox.handleInputChange}
                    onFocus={event => {
                      onFocus?.(event);
                      onFieldFocus?.('month');
                      setFocusedField('month');
                      if (!isBlocked && !readOnly) {
                        monthCombobox.setIsOpen(true);
                        monthInputRef.current?.select();
                      }
                    }}
                    onBlur={() => {
                      onFieldBlur?.('month');
                      setFocusedField(null);
                      monthCombobox.commitInput();
                    }}
                    onClick={() => {
                      if (!isBlocked && !readOnly) monthCombobox.setIsOpen(true);
                    }}
                    onKeyDown={monthCombobox.handleInputKeyDown}
                    className={cn(
                      'm-0 box-border h-full w-full min-w-0 border-0 bg-transparent text-center text-[16px] font-medium leading-5 outline-none placeholder:text-[length:var(--field-placeholder-font-size)] placeholder:font-[var(--field-placeholder-font-weight)] placeholder:text-[var(--chekin-color-gray-1)]',
                      monthIndex !== null
                        ? 'text-[var(--chekin-color-brand-navy)]'
                        : 'text-[var(--chekin-color-gray-1)]',
                      (isBlocked || readOnly) && 'cursor-not-allowed',
                    )}
                  />
                  <ChevronDown
                    size={14}
                    onMouseDown={event => {
                      event.preventDefault();
                      if (isBlocked || readOnly) return;
                      monthCombobox.setIsOpen(prev => !prev);
                      monthInputRef.current?.focus();
                    }}
                    className={cn(
                      'shrink-0 cursor-pointer text-[var(--chekin-color-gray-2)] transition-transform duration-200',
                      monthCombobox.isOpen &&
                        'rotate-180 text-[var(--chekin-color-brand-blue)]',
                    )}
                  />
                </div>
                <div className="flex h-full min-w-0 items-center px-2">
                  <input
                    ref={yearInputRef}
                    id={yearId}
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    name={name ? `${name}-year` : undefined}
                    value={year}
                    placeholder={yearPlaceholder}
                    disabled={isBlocked}
                    readOnly={readOnly}
                    aria-invalid={isInvalid}
                    aria-describedby={errorDescribedBy}
                    aria-labelledby={labelId}
                    onChange={handleYearChange}
                    onFocus={event => {
                      onFocus?.(event);
                      onFieldFocus?.('year');
                      setFocusedField('year');
                    }}
                    onBlur={() => {
                      onFieldBlur?.('year');
                      setFocusedField(null);
                    }}
                    maxLength={4}
                    className={subInputClass}
                  />
                </div>
              </div>
            )}
            {showCoverage && (
              <div
                className="absolute inset-0 cursor-text rounded-[6px] bg-[var(--empty-field-background)]"
                onClick={focusDayInput}
                aria-hidden="true"
              />
            )}
            <Fieldset
              isFocused={isFocused}
              invalid={isInvalid}
              isEmpty={isEmpty}
              isActivated={!isEmpty || isFocused}
              disabled={disabled}
              loading={loading}
              readOnly={readOnly}
              htmlFor={dayId}
              labelId={labelId}
              legend={label}
              label={label}
              tooltip={tooltip}
              onClick={isMobile ? openWheel : showCoverage ? focusDayInput : undefined}
            />
            {monthCombobox.isOpen && !isMobile && (
              <div className="absolute left-0 right-0 top-full z-30 mx-auto mt-1 w-full max-w-[260px] overflow-hidden rounded-md bg-white shadow-[0_30px_30px_0_rgba(33,72,255,0.2)] sm:left-1/2 sm:right-auto sm:-translate-x-1/2">
                {monthPanelContent}
              </div>
            )}
          </div>
          {isMobile && (
            <AirbnbDatePickerContent
              baseId={wheelBaseId}
              open={isWheelOpen}
              isMobile
              label={label ?? t('pick_date')}
              title={mobileTitle ?? label ?? t('pick_date')}
              doneLabel={resolvedDoneLabel}
              monthLabels={wheel.months.map(m => resolvedMonthLabels[m] ?? '')}
              days={wheel.days}
              years={wheel.years}
              monthIndex={wheel.monthIndex}
              dayIndex={wheel.dayIndex}
              yearIndex={wheel.yearIndex}
              monthScrollTop={wheel.monthScrollTop}
              dayScrollTop={wheel.dayScrollTop}
              yearScrollTop={wheel.yearScrollTop}
              monthListRef={wheel.monthListRef}
              dayListRef={wheel.dayListRef}
              yearListRef={wheel.yearListRef}
              onOpenChange={next => (next ? openWheel() : closeWheel())}
              onDone={handleWheelDone}
              onColumnScroll={wheel.handleColumnScroll}
              onColumnKeyDown={wheel.handleColumnKeyDown}
              onOptionSelect={wheel.handleOptionSelect}
            />
          )}
          {!resolvedError && optional && (
            <span className="mt-[1px] block text-left text-[14px] font-medium text-[var(--chekin-color-gray-1)]">
              {typeof optional === 'string' ? optional : t('optional')}
            </span>
          )}
          {!resolvedError && helperText && (
            <span className="mt-[1px] block text-[12px] font-normal text-[var(--chekin-color-gray-1)]">
              {helperText}
            </span>
          )}
          {resolvedError && !hideErrorMessage && (
            <FieldErrorMessage
              id={errorId}
              message={resolvedError}
              className="mt-[1px] text-[14px]"
            />
          )}
        </div>
      </div>
    );
  },
);

export {Datepicker};
export type {DatepickerProps, DatepickerDate, DatepickerSubfield, DatepickerHandle};
